<template>
    <div class="d-flex align-items-center">
        <b-button
            class="mr-2"
            title="inquiry"
            variant="primary"
            v-if="inquiryLink"
            :href="inquiryLink"
            :disabled="!reservationEdit"
        >
            <font-awesome-icon :icon="['fas', 'comments']" />
        </b-button>
        <b-button
            v-if="params.data.booking_method !== 'AIRBNB' || params.data.active"
            class="mr-2"
            title="edit"
            variant="warning"
            :disabled="!reservationEdit"
            @click="onEdit"
        >
            <font-awesome-icon :icon="['fas', 'pen-to-square']" />
        </b-button>
        <b-button
            class="mr-2"
            title="cancel"
            variant="danger"
            v-if="reservationCancel && params.data.state !== 'Cancelled'"
            :disabled="!reservationEdit"
            @click="onCancel"
        >
            <font-awesome-icon :icon="['fas', 'xmark']" />
        </b-button>
        <b-button
            title="delete"
            variant="danger"
            v-if="reservationDelete"
            :disabled="!reservationEdit"
            @click="$bvModal.show(deleteReservationModalId)"
        >
            <font-awesome-icon :icon="['fas', 'trash']" />
        </b-button>
        <validation-observer ref="cancel_form" v-slot="{ meta, setFieldValue, errors }" as="span">
            <modal
                :id="cancelReservationModalId"
                title="Cancel Reservation"
                ok-title="Proceed"
                cancel-title="Close"
                @ok.prevent="
                    () =>
                        params.data.booking_method === 'HOMEAWAY'
                            ? showCancellationConfirmationModal()
                            : cancelReservation()
                "
                :ok-disabled="!meta.valid"
                @shown="setCancelData"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-row v-if="params.data.has_published_owner_statement">
                    <b-col>
                        <b-alert variant="warning" show>
                            Warning! This invoice has already been reconciled to a published owner statement. Any
                            changes will not be reflected on current owner statements.
                        </b-alert>
                    </b-col>
                </b-row>
                <b-row class="past-reservation-warning mb-4" v-if="displayPastWarning">
                    <b-col>
                        {{ warningMessage }}
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-input floatingLabel="R. Total" v-model="rTotal" :disabled="true"></form-input>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-input floatingLabel="R. Deposit" v-model="rDeposit" :disabled="true"></form-input>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-input floatingLabel="Paid to date" v-model="paidToDate" :disabled="true"></form-input>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider name="cancellationFee" rules="validFloat|required">
                            <form-input
                                min="0"
                                step="0.01"
                                type="number"
                                floatingLabel="Cancel. Fee"
                                v-model="cancellationFee"
                                :error="errors.cancellationFee"
                                @input="(val) => setFieldValue('cancellationFee', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            floatingLabel="Record Refund"
                            v-model="recordRefund"
                            :options="recordRefundOptions"
                            @change="setCancellationRefund"
                        ></form-select>
                    </b-col>
                    <b-col>
                        <validation-provider
                            name="cancellationRefund"
                            :rules="recordRefund ? `validPositiveFloat|required` : ''"
                        >
                            <form-input
                                min="0"
                                step="0.01"
                                type="number"
                                floatingLabel="Cancellation Refund"
                                v-model="cancellationRefund"
                                :disabled="!recordRefund"
                                :error="errors.cancellationRefund"
                                @input="(val) => setFieldValue('cancellationRefund', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            floatingLabel="Record Credit"
                            v-model="recordCredit"
                            :options="recordCreditOptions"
                            @change="setCancellationCredit"
                        ></form-select>
                    </b-col>
                    <b-col>
                        <validation-provider
                            name="cancellationCredit"
                            :rules="recordCredit ? `validPositiveFloat|required` : ''"
                        >
                            <form-input
                                min="0"
                                step="0.01"
                                type="number"
                                floatingLabel="Cancellation Credit"
                                v-model="cancellationCredit"
                                :disabled="!recordCredit"
                                :error="errors.cancellationCredit"
                                @input="(val) => setFieldValue('cancellationCredit', val)"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="recordCredit">
                    <b-col>
                        <form-select floatingLabel="Send to Guest" v-model="sendToGuest" :options="sendToGuestOptions">
                        </form-select>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-select
                            floatingLabel="Status"
                            v-model="cancellationStatus"
                            :options="statusOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row v-if="recordRefund">
                    <b-col>
                        <form-select
                            floatingLabel="Payment Method"
                            v-model="paymentMethod"
                            :options="paymentMethodOptions"
                        ></form-select>
                    </b-col>
                </b-row>
                <b-row v-if="paymentMethod == 1">
                    <b-col>
                        <validation-provider name="extraData">
                            <form-input
                                floatingLabel="Check #"
                                v-model="extraData"
                                :error="errors.extraData"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="paymentMethod == 2">
                    <b-col>
                        <validation-provider name="extraData">
                            <form-input
                                floatingLabel="Certificate #"
                                v-model="extraData"
                                :error="errors.extraData"
                            ></form-input>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row v-if="recordRefund">
                    <b-col>
                        <form-textarea floatingLabel="Notes" v-model="notes"> </form-textarea>
                    </b-col>
                </b-row>
                <b-row v-if="recordRefund && paymentMethod == 6">
                    <b-col>
                        <label class="text-danger"
                            >Credit Card refunds MUST be processed through the gateway. Refunds are only recorded in
                            Lodgix.</label
                        >
                    </b-col>
                </b-row>
            </modal>
        </validation-observer>
        <validation-observer ref="airbnb_cancel_form" v-slot="{ meta: airnbnMeta, setFieldValue, errors }">
            <modal
                :id="cancelAirbnbReservationModalId"
                title="Cancel Reservation"
                ok-title="Proceed"
                cancel-title="Close"
                @ok.prevent="showCancellationConfirmationModal"
                :ok-disabled="!airnbnMeta.valid"
                @shown="setAirbnbCancelData"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-row>
                    <b-col>
                        <form-input
                            floatingLabel="Penalty Amount"
                            v-model="penaltyAmount"
                            :disabled="true"
                            class="mb-0"
                        ></form-input>
                    </b-col>
                </b-row>
                <small class="text-danger mb-3 d-block">
                    Airbnb may assess this penalty to host as a result of canceling this reservation.
                </small>
                <b-row>
                    <b-col>
                        <validation-provider name="reason" rules="required">
                            <form-select
                                floatingLabel="Reason"
                                v-model="reason"
                                :options="reasonsOptions"
                                :error="errors.reason"
                                @input="(val) => setFieldValue('reason', val)"
                            ></form-select>
                        </validation-provider>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <form-textarea
                            floatingLabel="Message to Guest"
                            v-model="messageToGuest"
                            rows="3"
                        ></form-textarea>
                    </b-col>
                </b-row>
                <b-row>
                    <b-col>
                        <validation-provider
                            name="messageToAirbnb"
                            :rules="{
                                required: isMessageToAirbnbRequired,
                            }"
                        >
                            <form-textarea
                                floatingLabel="Message to Airbnb"
                                v-model="messageToAirbnb"
                                rows="3"
                                :error="errors.messageToAirbnb"
                                @input="(val) => setFieldValue('messageToAirbnb', val)"
                            ></form-textarea>
                        </validation-provider>
                    </b-col>
                </b-row>
            </modal>
        </validation-observer>
        <modal
            :id="cancellationConfirmationModalId"
            title="Cancellation confirmation"
            ok-title="Proceed"
            cancel-title="Close"
            @ok="() => (params.data.booking_method === 'AIRBNB' ? cancelAirbnbReservation() : cancelReservation())"
        >
            <template v-if="params.data.booking_method === 'AIRBNB'">
                Please read carefully the Airbnb article on Host Cancellation Policies
                <a href="https://www.airbnb.com/help/article/990" target="_blank">
                    https://www.airbnb.com/help/article/990</a
                >. In general, the host will be assessed a fee for a host cancellation. Additionally, more than three
                host cancellations a year could result in the suspension of your Airbnb account. Please proceed with
                caution.
            </template>
            <template v-else-if="params.data.booking_method === 'HOMEAWAY'">
                Warning! VRBO now assesses cancellation fees for all partner / host initiated cancellations. To avoid
                these fees all VRBO reservations need to be canceled by the guest. To learn more about VRBO's partner
                initiated cancellation fees, please visit:
                <a href="https://help.vrbo.com/articles/Partner-Cancellation-Fee-Policy" target="_blank">
                    https://help.vrbo.com/articles/Partner-Cancellation-Fee-Policy
                </a>
            </template>
        </modal>
        <modal
            :id="deletionConfirmationModalId"
            title="Deletion confirmation"
            ok-title="Proceed"
            cancel-title="Close"
            @ok="deleteReservation"
        >
            Warning! VRBO now assesses cancellation fees for all partner / host initiated cancellations. To avoid these
            fees all VRBO reservations need to be canceled by the guest. To learn more about VRBO's partner initiated
            cancellation fees, please visit:
            <a href="https://help.vrbo.com/articles/Partner-Cancellation-Fee-Policy" target="_blank">
                https://help.vrbo.com/articles/Partner-Cancellation-Fee-Policy
            </a>
        </modal>
        <validation-observer ref="delete_form" v-slot="{ meta, setFieldValue, errors }">
            <modal
                :id="deleteReservationModalId"
                title="Delete Reservation"
                ok-title="Delete"
                cancel-title="Close"
                @ok.prevent="
                    () =>
                        params.data.booking_method === 'HOMEAWAY' && params.data.state !== 'Cancelled'
                            ? showDeletionConfirmationModal()
                            : deleteReservation()
                "
                :ok-disabled="!meta.valid"
                :hide-footer="params.data.has_published_owner_statement"
                @shown="setDeleteData"
            >
                <busy-modal :show="loading"></busy-modal>
                <b-row v-if="params.data.has_published_owner_statement">
                    <b-col>
                        <b-alert variant="danger" show>
                            This invoice has already been reconciled to a published owner statement and cannot be
                            deleted.
                        </b-alert>
                    </b-col>
                </b-row>
                <template v-else>
                    <b-row class="past-reservation-warning mb-4" v-if="displayPastWarning">
                        <b-col>
                            {{ warningMessage }}
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col>
                            <validation-provider name="password" rules="required">
                                <form-input
                                    type="password"
                                    v-model="password"
                                    label="Please enter your system password to confirm reservation deletion"
                                    :error="errors.password"
                                    @input="(val) => setFieldValue('password', val)"
                                ></form-input>
                            </validation-provider>
                        </b-col>
                    </b-row>
                </template>
            </modal>
        </validation-observer>
    </div>
</template>

<script>
    import { Decimal } from 'decimal.js';
    import { mapGetters } from 'vuex';
    import isEmpty from 'lodash/isEmpty';
    import API from '../../../api.jsx';
    import Modal from '../../generic/modal/modal.vue';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormSelect from '../../generic/form-select/form-select.vue';
    import FormTextarea from '../../generic/form-textarea/form-textarea.vue';
    import { formatResponseError } from '../../../utils/api';
    import {
        GUEST_RESERVATION_AIRBNB_CANCELLATION_DATA_ENDPOINT,
        GUEST_RESERVATION_CANCEL_ENDPOINT,
        GUEST_RESERVATION_CANCEL_AIRBNB_ENDPOINT,
        GUEST_RESERVATION_DELETE_ENDPOINT,
    } from '../../../endpoints';

    export default {
        components: { Modal, BusyModal, FormInput, FormSelect, FormTextarea },
        data() {
            return {
                guestId: null,
                loading: false,
                cancellationConfirmationModalId: null,
                cancelAirbnbReservationModalId: null,
                cancelReservationModalId: null,
                deletionConfirmationModalId: null,
                deleteReservationModalId: null,
                password: null,
                editLink: '',
                rTotal: 0,
                rDeposit: 0,
                paidToDate: 0,
                cancellationFee: 0,
                recordRefund: false,
                refundPercent: 0,
                cancellationRefund: 0,
                warningMessage:
                    'Damage insurance on an invoice where the check-in has already occurred cannot be canceled.' +
                    ' The invoice can be deleted, but the policy is not refundable.',
                extraData: '',
                recordRefundOptions: [
                    { value: false, text: 'No' },
                    { value: true, text: 'Yes' },
                ],
                recordCredit: false,
                cancellationCredit: 0,
                recordCreditOptions: [
                    { value: false, text: 'No' },
                    { value: true, text: 'Yes' },
                ],
                sendToGuest: true,
                sendToGuestOptions: [
                    { value: true, text: 'Email credit code to guest' },
                    { value: false, text: 'Do not email credit code to guest' },
                ],
                cancellationStatus: 'CANCELLED_BY_TRAVELER',
                statusOptions: [
                    {
                        value: 'CANCELLED_BY_OWNER',
                        text: 'Cancelled by Subscriber',
                    },
                    { value: 'CANCELLED_BY_TRAVELER', text: 'Cancelled by Guest' },
                ],
                paymentMethod: 6,
                bookingMethod: '',
                paymentMethodOptions: [],
                isCancellationPolicySet: false,
                displayPastWarning: false,
                inquiryLink: false,
                notes: '',
                isAirbnbCancellationDataWasUploaded: false,
                penaltyAmount: 0,
                applicableReasons: [],
                reason: '',
                messageToGuest: '',
                messageToAirbnb: '',
            };
        },
        inject: ['showSuccessToast', 'showErrorToast'],
        created() {
            this.guestId = this.params.guestId;
            this.cancelAirbnbReservationModalId = `cancel-airbnb-reservation-modalId-${this.params.data.id}`;
            this.cancellationConfirmationModalId = `cancellation-confirmation-modalId-${this.params.data.id}`;
            this.cancelReservationModalId = `cancel-reservation-modalId-${this.params.data.id}`;
            this.deletionConfirmationModalId = `deletion-confirmation-modalId-${this.params.data.id}`;
            this.deleteReservationModalId = `delete-reservation-modalId-${this.params.data.id}`;
            this.editLink = this.params.data.edit_link;
            this.rTotal = this.params.data.cancel_data.whole_sum;
            this.rDeposit = this.params.data.cancel_data.reservation_deposit;
            this.paidToDate = this.params.data.cancel_data.reservation_sum_paid;
            this.isCancellationPolicySet = this.params.data.cancel_data.refund_percent > 0;
            this.recordRefund = this.params.data.cancel_data.refund_percent > 0;
            this.refundPercent = this.params.data.cancel_data.refund_percent;
            this.cancellationFee = this.params.data.cancel_data.cancellation_fee;
            this.cancellationRefund = this.refundSum;
            this.inquiryLink = this.params.data.inquiry_link;
            this.bookingMethod = this.params.data.booking_method;
            this.displayPastWarning = this.params.data.display_past_warning;
            this.paymentMethodOptions = this.metadata.record_payment_method_choices;
        },
        computed: {
            ...mapGetters('guest', ['metadata']),
            ...mapGetters('reservationsStore', ['reservationDelete', 'reservationEdit']),
            refundSum() {
                return this.toDecimal(((this.paidToDate - this.cancellationFee) * this.refundPercent) / 100);
            },
            reservationEdit() {
                return this.metadata?.permissions?.reservation_edit;
            },
            reservationCancel() {
                return this.metadata?.permissions?.reservation_cancel;
            },
            reservationDelete() {
                return this.metadata?.permissions?.reservation_delete;
            },
            reasonsOptions() {
                const defaultOption = {
                    value: '',
                    text: '-- Please select an option --',
                    disabled: true,
                };
                if (this.applicableReasons) {
                    return [
                        defaultOption,
                        ...this.applicableReasons.map((applicableReason) => {
                            return {
                                value: applicableReason.value,
                                text: applicableReason.description,
                            };
                        }),
                    ];
                }
                return [defaultOption];
            },
            isMessageToAirbnbRequired() {
                if (!!this.reason) {
                    return this.applicableReasons.find((applicableReason) => {
                        return this.reason === applicableReason.value;
                    }).require_message_to_airbnb;
                }
                return false;
            },
        },
        methods: {
            setCancellationRefund() {
                if (this.isCancellationPolicySet) {
                    if (this.recordRefund && this.recordCredit) this.cancellationRefund = 0;
                    else if (this.recordRefund) {
                        this.cancellationRefund = this.refundSum;
                    } else this.cancellationRefund = 0;
                } else {
                    if (this.recordRefund && this.recordCredit) {
                        this.cancellationRefund = this.toDecimal(
                            Math.max(this.paidToDate - this.cancellationCredit, 0)
                        );
                    } else if (this.recordRefund) {
                        this.cancellationRefund = this.toDecimal(Math.max(this.paidToDate - this.cancellationFee, 0));
                    } else this.cancellationRefund = 0;
                }
                this.$refs.cancel_form.setFieldValue('cancellationRefund', this.cancellationRefund);
            },
            setCancellationCredit() {
                if (this.isCancellationPolicySet) {
                    if (this.recordCredit && this.recordRefund) this.cancellationCredit = 0;
                    else if (this.recordCredit) {
                        this.cancellationCredit = this.refundSum;
                    } else this.cancellationCredit = 0;
                } else {
                    if (this.recordCredit && this.recordRefund) {
                        this.cancellationCredit = this.toDecimal(
                            Math.max(this.paidToDate - this.cancellationRefund, 0)
                        );
                    } else if (this.recordCredit) {
                        this.cancellationCredit = this.toDecimal(Math.max(this.paidToDate - this.cancellationFee, 0));
                    } else this.cancellationCredit = 0;
                }
                this.$refs.cancel_form.setFieldValue('cancellationCredit', this.cancellationCredit);
            },
            setCancelData() {
                this.$refs.cancel_form.setValues({
                    cancellationFee: this.cancellationFee,
                    cancellationRefund: this.cancellationRefund,
                    cancellationCredit: this.cancellationCredit,
                });
            },
            setAirbnbCancelData() {
                this.$refs.airbnb_cancel_form.setValues({
                    reason: this.reason,
                    messageToAirbnb: this.messageToAirbnb,
                });
            },
            setDeleteData() {
                this.$refs.delete_form.setValues({
                    password: this.password,
                });
            },
            async deleteReservation() {
                try {
                    this.loading = true;
                    await API.post(GUEST_RESERVATION_DELETE_ENDPOINT(this.params.data.id, this.guestId), {
                        password: this.password,
                    });
                    this.$bvModal.hide(this.deleteReservationModalId);
                    await this.params.refreshAfterDelete();
                    this.showSuccessToast({
                        text: 'Reservation has been deleted.',
                    });
                } catch (e) {
                    const { generic, fields } = formatResponseError(e);
                    if (!isEmpty(fields)) this.$refs.delete_form.setErrors(fields);
                    this.showErrorToast({
                        text: generic || 'Could not delete reservation.',
                    });
                } finally {
                    this.loading = false;
                }
            },
            showCancellationConfirmationModal() {
                this.$bvModal.show(this.cancellationConfirmationModalId);
            },
            showDeletionConfirmationModal() {
                this.$bvModal.show(this.deletionConfirmationModalId);
            },
            async cancelReservation() {
                try {
                    this.loading = true;
                    const { data } = await API.post(
                        GUEST_RESERVATION_CANCEL_ENDPOINT(this.params.data.id, this.guestId),
                        {
                            send_to_guest: this.sendToGuest,
                            payment_method: this.paymentMethod,
                            cancellation_fee: this.cancellationFee,
                            cancellation_refund: this.cancellationRefund,
                            cancellation_credit: this.cancellationCredit,
                            cancellation_status: this.cancellationStatus,
                            extra_data: this.extraData,
                            notes: this.notes,
                        }
                    );
                    this.$bvModal.hide(this.cancelReservationModalId);
                    await this.params.refresh();
                    if (data) this.warningToast({ text: data });
                    else {
                        this.showSuccessToast({
                            text: 'Reservation has been cancelled.',
                        });
                    }
                } catch (e) {
                    const { generic, fields } = formatResponseError(e);
                    if (!isEmpty(fields)) this.$refs.cancel_form.setErrors(fields);
                    this.showErrorToast({
                        text: generic || 'Could not cancel reservation.',
                    });
                } finally {
                    this.loading = false;
                }
            },
            async cancelAirbnbReservation() {
                try {
                    this.loading = true;
                    await API.post(GUEST_RESERVATION_CANCEL_AIRBNB_ENDPOINT(this.params.data.id, this.guestId), {
                        reason: this.reason,
                        message_to_guest: this.messageToGuest,
                        message_to_airbnb: this.messageToAirbnb,
                    });
                    this.$bvModal.hide(this.cancelAirbnbReservationModalId);
                    this.penaltyAmount = 0;
                    this.reason = '';
                    this.messageToGuest = '';
                    this.messageToAirbnb = '';
                    this.isAirbnbCancellationDataWasUploaded = false;
                    this.showSuccessToast({
                        text:
                            'The reservation request was successfully sent to Airbnb. The status of ' +
                            'this reservation will be updated once Lodgix receives confirmation from Airbnb.',
                    });
                } catch (e) {
                    const { generic, fields } = formatResponseError(e);
                    if (!isEmpty(fields)) this.$refs.airbnb_cancel_form.setErrors(fields);
                    this.showErrorToast({
                        text: generic || 'Could not cancel reservation.',
                    });
                } finally {
                    this.loading = false;
                }
            },
            async onCancel() {
                if (this.bookingMethod == 'AIRBNB') {
                    if (this.isAirbnbCancellationDataWasUploaded) {
                        this.$bvModal.show(this.cancelAirbnbReservationModalId);
                    } else {
                        try {
                            this.params.handleLoading(true);
                            const { data } = await API.get(
                                GUEST_RESERVATION_AIRBNB_CANCELLATION_DATA_ENDPOINT(this.params.data.id, this.guestId)
                            );
                            this.applicableReasons = data.applicable_reasons;
                            this.penaltyAmount = data.penalty_amount_usd_accurate;
                            this.isAirbnbCancellationDataWasUploaded = true;
                            this.$bvModal.show(this.cancelAirbnbReservationModalId);
                        } catch (e) {
                            const { generic, fields } = formatResponseError(e);
                            if (!isEmpty(fields)) this.$refs.airbnb_cancel_form.setErrors(fields);
                            this.showErrorToast({
                                text: generic || 'Could not cancel reservation.',
                            });
                        } finally {
                            this.params.handleLoading(false);
                        }
                    }
                } else {
                    this.$bvModal.show(this.cancelReservationModalId);
                }
            },
            toDecimal(amount) {
                return Number(Decimal(amount).toFixed(2, Decimal.ROUND_HALF_EVEN));
            },
            onEdit() {
                this.$router.push(this.editLink);
            },
        },
    };
</script>

<style lang="scss" scoped>
    .past-reservation-warning {
        font-weight: bold;
        text-align: center;
        font-size: 11px;
    }
    :deep(.btn) {
        min-width: 2.7rem !important;
    }
    :deep(.btn:focus) {
        box-shadow: none !important;
    }
</style>
